'use client';

import { type ReactNode, useState } from 'react';

function useContainerWidth() {
  const [width, setWidth] = useState<number | null>(null);

  const ref = (node: HTMLDivElement | null) => {
    if (node) {
      setWidth(node.offsetWidth);
      const observer = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) setWidth(entry.contentRect.width);
      });
      observer.observe(node);
      return () => observer.disconnect();
    }
  };

  return { ref, width };
}

/**
 * Defers rendering until the container width is measured via
 * ResizeObserver. Before measurement, renders the optional `fallback`
 * and reserves space using the given `aspectRatio` so the layout
 * doesn't shift when content appears.
 */
export function MeasuredContainer({
  children,
  fallback,
  aspectRatio,
  className,
}: {
  children: (width: number) => ReactNode;
  fallback?: ReactNode;
  /** CSS aspect-ratio applied before measurement to reserve space. */
  aspectRatio?: string;
  className?: string;
}) {
  const { ref, width } = useContainerWidth();

  return (
    <div
      ref={ref}
      className={className ?? 'min-w-0'}
      style={width == null && aspectRatio ? { aspectRatio } : undefined}
    >
      {width != null ? children(width) : fallback}
    </div>
  );
}
